import { useShopGroupStore } from "@/stores/shopgroup";
import { useUserSecurityStore } from "@/stores/usersecurity";

// ตัวช่วยกลุ่มสาขา — ใช้ร่วมกันระหว่าง ตั้งค่า > กลุ่มสาขา กับ dashboard
// (tab ภาพรวม / ตั๋วครบกำหนด) — concept เดียวกับ iBuddy Anywhere

export const MIN_SHOPS_PER_GROUP = 2;

// query หลายสาขาฝั่ง server ยังช้า — ยิง API ที่รับทีละสาขาพร้อมกันไม่เกินเท่านี้
const PER_SHOP_CONCURRENCY = 3;

let allowedUidsCache = null;
let introspectCache = null;

export function clearShopGroupCache() {
  allowedUidsCache = null;
  introspectCache = null;
}

function unwrap(response) {
  if (!response || response.status !== 200) {
    throw new Error("HTTP " + (response ? response.status : "-"));
  }
  const body = response.data;
  if (!body) throw new Error("Empty response body");
  if (body.rtnDesc !== "success") {
    if (String(body.rtnCode) === "101") {
      throw new Error("กลุ่มนี้ถูกแก้ไขจากที่อื่นไปแล้ว กรุณาโหลดใหม่แล้วลองอีกครั้ง");
    }
    if (String(body.rtnCode) === "104") {
      throw new Error("บัญชีนี้ใช้งานกลุ่มสาขาไม่ได้");
    }
    throw new Error("rtnCode=" + body.rtnCode + " rtnDesc=" + body.rtnDesc);
  }
  return body.data;
}

// ข้อมูล introspect (cache ไว้ตลอดอายุหน้า — introspect ครั้งเดียวพอ)
export async function getIntrospect() {
  if (introspectCache) return introspectCache;
  const userSStore = useUserSecurityStore();
  const key = JSON.parse(localStorage.getItem("FR-SDK-nvtx-admin") || "null");
  const token = key?.accessToken || "";
  try {
    const res = await userSStore.introspect(token);
    introspectCache = res || {};
  } catch (e) {
    console.error("introspect in shopGroup error:", e);
    introspectCache = {};
  }
  return introspectCache;
}

export async function getCurrentUserUid() {
  const res = await getIntrospect();
  return res.user_uid || res.data?.user_uid || "";
}

// shop_uids จาก introspect
export async function getAllowedShopUids() {
  if (allowedUidsCache) return allowedUidsCache;
  const res = await getIntrospect();
  const raw = res.shop_uids || res.data?.shop_uids || "";
  const list =
    typeof raw === "string"
      ? raw
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean)
      : Array.isArray(raw)
      ? raw
      : [];
  allowedUidsCache = new Set(list);
  return allowedUidsCache;
}

// สาขาที่เลือกเข้ากลุ่มได้ = shoplist (ที่ login เก็บไว้) ∩ shop_uids
export async function getAllowedShops() {
  const shops = JSON.parse(localStorage.getItem("shoplist") || "[]");
  const allowed = await getAllowedShopUids();
  if (!allowed || allowed.size === 0) return shops;
  return shops.filter(
    (s) =>
      allowed.has(s.uid) ||
      allowed.has(String(s.uid)) ||
      allowed.has(s.code)
  );
}

function normalizeGroup(data) {
  if (!data) return { uid: "", name: "", version: "0", members: [] };
  const rawMembers = Array.isArray(data.members) ? data.members : [];
  return {
    uid: String(data.uid || ""),
    name: data.name || "",
    version: data.version != null ? String(data.version) : "0",
    members: rawMembers.map((m) => {
      if (typeof m === "object" && m !== null) {
        return {
          uid: m.uid ? String(m.uid) : "",
          shopUid: String(m.shopUid ?? m.uid ?? ""),
          shopCode: m.shopCode || "",
          shopName: m.shopName || "",
        };
      }
      return {
        uid: "",
        shopUid: String(m),
        shopCode: "",
        shopName: "",
      };
    }),
  };
}

export async function getGroupInfo(uid) {
  const store = useShopGroupStore();
  const res = await store.shopGroupInfoFetch(uid);
  return normalizeGroup(unwrap(res));
}

// list ทุกกลุ่มพร้อม members
export async function listGroupsWithMembers() {
  const userUid = await getCurrentUserUid();
  const store = useShopGroupStore();
  const res = await store.shopGroupListFetch({
    index: 0,
    size: 100,
    userUid: userUid,
  });
  const data = unwrap(res);
  const list =
    (data && (data.list || (Array.isArray(data) ? data : []))) || [];
  return Promise.all(
    list.map(async (g) => {
      if (Array.isArray(g.members) && g.members.length > 0) {
        return normalizeGroup(g);
      }
      try {
        return await getGroupInfo(g.uid);
      } catch (err) {
        return normalizeGroup(g);
      }
    })
  );
}

// ตัดสาขาที่ user ไม่มีสิทธิ์แล้วออก (เช่น ถูกถอดสิทธิ์หลังตั้งกลุ่มไว้)
export function restrictGroup(group, allowedUids) {
  if (!allowedUids || allowedUids.size === 0) return group;
  return {
    ...group,
    members: (group.members || []).filter(
      (m) =>
        allowedUids.has(m.shopUid) ||
        allowedUids.has(m.shopCode) ||
        allowedUids.has(m.uid)
    ),
  };
}

// กลุ่มสำหรับตัวเลือก "ดูข้อมูลของ" — เอา list ที่ได้จาก /master-svc/shop-grp/list มาใช้ตรงๆ
export async function listSelectableGroups() {
  const userUid = await getCurrentUserUid();
  const store = useShopGroupStore();
  const res = await store.shopGroupListFetch({
    index: 0,
    size: 100,
    userUid: userUid,
  });
  let rawList = [];
  if (res?.data?.data?.list && Array.isArray(res.data.data.list)) {
    rawList = res.data.data.list;
  } else if (Array.isArray(res?.data?.data)) {
    rawList = res.data.data;
  } else if (Array.isArray(res?.data?.list)) {
    rawList = res.data.list;
  } else if (Array.isArray(res?.data)) {
    rawList = res.data;
  }
  return rawList.map((g) => ({
    uid: String(g.uid || ""),
    name: g.name || "",
    version: String(g.version ?? "1"),
    members: Array.isArray(g.members) ? g.members : [],
  }));
}

export async function saveGroup({ group, name, members }) {
  const store = useShopGroupStore();
  const body = members.map((m) => ({
    shopCode: m.shopCode || "",
    shopName: m.shopName || "",
    shopUid: String(m.shopUid || ""),
    ...(m.uid ? { uid: String(m.uid) } : {}),
  }));
  if (group) {
    unwrap(
      await store.shopGroupUpdateFetch({
        uid: group.uid,
        name,
        members: body,
        version: String(group.version ?? "1"),
      })
    );
  } else {
    unwrap(await store.shopGroupAddFetch({ name, members: body }));
  }
}

export async function deleteGroup(uid) {
  unwrap(await useShopGroupStore().shopGroupDeleteFetch(uid));
}

// เรียก fn(shop) ทีละสาขา พร้อมกันไม่เกิน PER_SHOP_CONCURRENCY สำหรับ API ที่
// รับได้ทีละ shopUid — onProgress(done, total) ทุกครั้งที่จบหนึ่งสาขา,
// isStale() = true เมื่อมีการโหลดรอบใหม่แล้ว (หยุดยิงต่อ)
// คืน { results: [{ shop, value }], failed: [shop] }
export async function runPerShop(shops, fn, { onProgress, isStale } = {}) {
  const results = [];
  const failed = [];
  let next = 0;
  let done = 0;
  async function worker() {
    while (next < shops.length) {
      const shop = shops[next++];
      try {
        const value = await fn(shop);
        if (isStale && isStale()) return;
        results.push({ shop, value });
      } catch (e) {
        if (isStale && isStale()) return;
        failed.push(shop);
      }
      done++;
      if (onProgress) onProgress(done, shops.length);
    }
  }
  const workers = Math.min(PER_SHOP_CONCURRENCY, shops.length);
  await Promise.all(Array.from({ length: workers }, () => worker()));
  return { results, failed };
}

// overdue/list ไม่ส่ง shopUid มาในแต่ละแถว — หาสาขาจากรหัสนำหน้าเลขตั๋ว
// (เช่น "NW-260700094" → NW) เทียบกับ shopCode ของสมาชิกในกลุ่ม
export function memberForRefNumber(group, refNumber) {
  if (!group || !refNumber) return null;
  const code = String(refNumber).split("-")[0];
  return (group.members || []).find((m) => m.shopCode === code) || null;
}
